import { useState } from 'react';
import axios from 'axios';
import { getApiUrl } from '../config/api';

export interface PayrollRecord {
  id: number;
  teacherId: number;
  periodId: number;
  baseSalary: number;
  housingAllowance: number;
  transportAllowance: number;
  responsibilityAllowance: number;
  otherAllowances: number;
  grossSalary: number;
  totalDeductions: number;
  netSalary: number;
  napsaDeduction: number;
  payeDeduction: number;
  nhimaDeduction: number;
  status: string;
  paymentDate: string | null;
  createdAt: string;
}

export const usePayrollActions = (onSuccess: () => void, setError: (error: string | null) => void, setSuccess: (message: string | null) => void) => {
  const [loading, setLoading] = useState(false);

  const handleDelete = async (id: number) => {
    if (!window.confirm('Are you sure you want to delete this payroll record?')) return;

    setLoading(true);
    try {
      const token = localStorage.getItem('token') || '';
      await axios.delete(getApiUrl(`payroll/${id}`), {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSuccess('Payroll deleted successfully');
      onSuccess();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Error deleting payroll');
    } finally {
      setLoading(false);
    }
  };

  const handleMarkAsPaid = async (id: number) => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token') || '';
      await axios.put(getApiUrl(`payroll/${id}/mark-paid`), {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSuccess('Payment marked as completed');
      onSuccess();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Error updating payment status');
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (id: number) => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token') || '';
      await axios.put(getApiUrl(`payroll/${id}/approve`), {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSuccess('Payroll approved successfully');
      onSuccess();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Error approving payroll');
    } finally {
      setLoading(false);
    }
  };

  const handleExportCSV = () => {
    // This will be implemented in the component
    // For now, just return a placeholder
    return () => {
      console.log('Export CSV functionality to be implemented');
    };
  };

  const handleBulkDelete = async (ids: number[]) => {
    if (ids.length === 0) return;
    if (!window.confirm(`Are you sure you want to delete ${ids.length} selected payroll records?`)) return;

    setLoading(true);
    try {
      const token = localStorage.getItem('token') || '';
      await axios.post(getApiUrl('payroll/bulk-delete'), { ids }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSuccess(`${ids.length} payroll records deleted successfully`);
      onSuccess();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Error bulk deleting payrolls');
    } finally {
      setLoading(false);
    }
  };

  return {
    loading,
    handleDelete,
    handleBulkDelete,
    handleMarkAsPaid,
    handleApprove,
    handleExportCSV
  };
};
